import React from "react";
import { ReactSVG } from "react-svg";
import { IRumbleFormScope } from ".";
import { useStateMachine } from "little-state-machine";
import { IFormStep } from "@formily/antd-v5";
import { theme } from "antd";
const { useToken } = theme;

interface IRumbleGridStepProps {
  icon?: React.ReactNode;
  iconUrl?: string;
  title: string;
  isActive?: boolean;
  step: number;
  showStepNumber?: boolean;
  description?: string;
}

export const RumbleGridStep = ({
  icon,
  iconUrl,
  title,
  description,
  step,
  isActive = false,
  showStepNumber = true,
}: IRumbleGridStepProps) => {
  const { state } = useStateMachine();
  const scope = (state as { scope?: IRumbleFormScope }).scope;
  const formStep = scope?.formStep as IFormStep;
  if (!formStep) {
    throw new Error("formStep function is not available");
  }
  const { token } = useToken();

  return (
    <button
      onClick={() => formStep.next()}
      className={`bg-white w-full p-6 pb-6 flex flex-col justify-center items-center hover:shadow-md transition duration-300 ease-in-out${isActive ? " border" : ""}`}
      style={
        isActive
          ? {
              borderColor: token.colorPrimary,
              borderWidth: 1,
              borderStyle: "solid",
              borderRadius: token.borderRadiusLG,
            }
          : {
              borderRadius: token.borderRadiusLG,
            }
      }
    >
      {iconUrl && (
        <ReactSVG
          beforeInjection={(svg) => {
            svg.setAttribute("style", `color: ${token.colorPrimary}`);
          }}
          src={iconUrl}
          className="w-12 h-12"
        />
      )}

      {icon ? icon : null}

      <h3 className="text-sm font-semibold text-black leading-4 mt-6 whitespace-pre-wrap text-center">
        {title}
      </h3>

      {description ? (
        <p className="text-xs text-gray-500 mt-2 text-center">{description}</p>
      ) : null}

      {showStepNumber ? (
        <span
          className="font-bold rounded-md w-6 h-6 flex items-center justify-center text-white mt-6 text-sm"
          style={{
            background: token.colorPrimary,
            opacity: !isActive ? 0.25 : 1,
          }}
        >
          {step}
        </span>
      ) : null}
    </button>
  );
};

const IconVehicleDamage = () => {
  const { token } = useToken();
  return (
    <svg
      width="60"
      height="52"
      viewBox="0 0 60 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        style={{ fill: token.colorPrimary }}
        d="M24.1917 25.6044L9.27304 13.4901H1.63226V17.6322H8.09845L17.3003 25.9629C17.7608 26.3798 17.4659 27.145 16.8447 27.145H1.63226V43.9011H32.4399H34.243L37.8764 39.9219V30.9886L36.8669 29.4182L24.1917 25.6044Z"
      ></path>
      <circle
        style={{ fill: token.colorPrimary }}
        cx="23.7668"
        cy="43.1343"
        r="8.43671"
      ></circle>
      <circle cx="23.7668" cy="43.1343" r="4.50249" fill="white"></circle>
      <path
        style={{ stroke: token.colorPrimary }}
        d="M25.0889 21.1176L21.0508 13.9388L31.5948 15.9578L35.1842 4.74081L40.344 14.8361L53.6923 8.77893L46.6255 20.5568L56.16 26.7261L45.9525 30.3156L47.2986 44.1125L41.9144 38.3918"
        strokeWidth="3"
        strokeLinecap="round"
      ></path>
    </svg>
  );
};

const IconCalendar = () => {
  const { token } = useToken();
  return (
    <svg
      width="57"
      height="49"
      viewBox="0 0 57 49"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        style={{ fill: token.colorPrimary }}
        x="5.82458"
        y="6.09232"
        width="44.8895"
        height="38.081"
        rx="5"
      ></rect>
      <path
        style={{ stroke: token.colorPrimary }}
        d="M18.2262 2.05322V9.60047"
        strokeWidth="3"
        strokeLinecap="round"
      ></path>
      <path
        style={{ stroke: token.colorPrimary }}
        d="M37.8764 2.05322V9.60047"
        strokeWidth="3"
        strokeLinecap="round"
      ></path>
      <path
        d="M0.972473 17.608L54.3896 17.608"
        stroke="white"
        strokeWidth="2"
      ></path>
      <circle cx="15.2717" cy="25.1328" r="2.61644" fill="white"></circle>
      <circle cx="15.2717" cy="34.3083" r="2.61644" fill="white"></circle>
      <circle cx="24.1347" cy="25.1328" r="2.61644" fill="white"></circle>
      <circle cx="32.9978" cy="25.1328" r="2.61644" fill="white"></circle>
      <circle cx="24.1347" cy="34.3083" r="2.61644" fill="white"></circle>
      <circle cx="32.9978" cy="34.3083" r="2.61644" fill="white"></circle>
      <circle cx="41.8608" cy="25.1328" r="2.61644" fill="white"></circle>
      <circle
        style={{ fill: token.colorPrimary }}
        cx="47.6364"
        cy="40.0839"
        r="8.39199"
      ></circle>
      <path
        d="M50.7141 37.7875L46.5891 41.9125L44.7141 40.0375"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  );
};

const IconDocumentSearch = () => {
  const { token } = useToken();
  return (
    <svg
      width="47"
      height="40"
      viewBox="0 0 47 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        style={{ fill: token.colorPrimary }}
        x="0.776855"
        y="0.490051"
        width="28.8437"
        height="38.081"
        rx="5"
      ></rect>
      <path
        d="M6.57648 11.2162H24.742M6.57648 16.4416H22.2715"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      ></path>
      <path
        d="M6.57648 21.6976H14.2823"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      ></path>
      <circle
        cx="27.5106"
        cy="21.0775"
        r="7.27195"
        transform="rotate(-45 27.5106 21.0775)"
        fill="white"
        strokeWidth="3"
      ></circle>
      <path
        style={{ stroke: token.colorPrimary }}
        d="M31.8132 25.3801L41.6989 35.2658"
        strokeWidth="2"
      ></path>
      <path
        style={{ stroke: token.colorPrimary }}
        d="M37.4631 31.03L44.1359 37.7029"
        strokeWidth="4"
        strokeLinecap="round"
      ></path>
    </svg>
  );
};

const IconVehicleCheck = () => {
  const { token } = useToken();
  return (
    <svg
      width="44"
      height="39"
      viewBox="0 0 44 39"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        style={{ fill: token.colorPrimary }}
        d="M23.4659 12.9239L8.54727 0.809509H0.906487V4.95164H7.37268L16.5745 13.2823C17.035 13.6992 16.7401 14.4644 16.1189 14.4644H0.906487V31.2206H31.7142H33.5172L37.1506 27.2414V18.308L36.1411 16.7376L23.4659 12.9239Z"
      ></path>
      <circle
        style={{ fill: token.colorPrimary }}
        cx="23.0411"
        cy="30.4537"
        r="8.43671"
      ></circle>
      <circle cx="23.041" cy="30.4538" r="4.50249" fill="white"></circle>
      <circle
        style={{ fill: token.colorPrimary }}
        cx="34.8131"
        cy="11.0839"
        r="8.39199"
      ></circle>
      <path
        d="M37.8909 8.78748L33.7659 12.9125L31.8909 11.0375"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  );
};

interface RumbleWelcomeProps {
  order?: string[];
}

export const RumbleWelcome: React.FC<RumbleWelcomeProps> = ({
  order = ["damage", "servicePartner", "personalInfo", "review"],
}) => {
  const stepComponents: Record<string, (isActive: boolean) => JSX.Element> = {
    damage: (isActive) => (
      <RumbleGridStep
        step={order.indexOf("damage") + 1}
        icon={<IconVehicleDamage />}
        title={"Schade\ngegevens"}
        isActive={isActive}
      />
    ),
    servicePartner: (isActive) => (
      <RumbleGridStep
        step={order.indexOf("servicePartner") + 1}
        icon={<IconCalendar />}
        title={"Schadehersteller\nselecteren"}
        isActive={isActive}
      />
    ),
    personalInfo: (isActive) => (
      <RumbleGridStep
        step={order.indexOf("personalInfo") + 1}
        icon={<IconDocumentSearch />}
        title={"Persoonlijke\ngegevens"}
        isActive={isActive}
      />
    ),
    review: (isActive) => (
      <RumbleGridStep
        step={order.indexOf("review") + 1}
        icon={<IconVehicleCheck />}
        title={"Controleer\nen verstuur"}
        isActive={isActive}
      />
    ),
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-6">
        {order.map((stepId, index) => (
          <React.Fragment key={stepId}>
            {stepComponents[stepId](index === 0)}
          </React.Fragment>
        ))}
      </div>
    </>
  );
};

export default RumbleWelcome;
